import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { getCalendar, updatePersonalDateInfo } from "page/calendar/store";
import { withRouter, RouteComponentProps, Redirect } from "react-router-dom";
import queryString from "query-string";
import {
    CalendarState,
    MonthDivider as MonthDividerType,
    ExtraPage,
    CustomImage,
    RubberBandItem,
    SpiralItem,
    ImageDetails,
} from "burde";
import {
    actionCreators as uiActionCreators,
    getActiveNavigationComponent,
    getDomain,
    getFastTrackStatus,
    getPreviewedExtraPage,
    getRubberbandItem,
    getSpiralItem,
} from "ui/store";
import { actionCreators as userActionCreators } from "user/store";
import {
    actionCreators as burdeApiContentActionCreators,
    getIsLoadingLayoutOptions,
    getBackCoverImage,
    getCoverImage,
    getCustomBackImage,
    getCustomCoverImage,
    getMonthDividerInfo,
    getBorderImage,
} from "burde-api-content/store";
import { actionCreators as eventActionCreators } from "event/store";
import CalendarCover from "page/calendar/step/2-cover";
import OwnerPage from "page/calendar/step/3-owner-page/owner-page";
import CalendarInsert from "page/calendar/step/3-insert";
import CalendarSummary from "page/calendar/step/5-summary";
import { actionCreators } from "page/calendar/store";
import { useResources } from "contexts/resource-context";
import {
    NavigationComponents,
    DefaultSize,
    CalendarSteps,
    NavigationComponent,
    LOCAL_STORAGE_CONFIGURATION,
    LocalStorageTemporaryCalendar,
    MaxTemporaryCalendarStorageHours,
    COOKIE_USER_JWT,
    COOKIE_USER_EXPIRE_DAYS,
} from "ui/constants";
import * as moment from "moment";
import { createTemporaryCalendar, getDatesets } from "calendar/service";
import ExtraPages from "./step/extra-pages/extra-pages";
import MonthDivider from "./step/month-divider/month-divider";
import { OwnerPage as OwnerPageType } from "burde";
import { getComponentRoute } from "./helper";
import { ApplicationState } from "store";
import { saveCalendar } from "calendar/service";
import { validateEmail } from "ui/helper";
import CalendarUrlHandler from "./calendar-url-handler";
import { isTokenExpired } from "util/jwt-token";
import { getIsAuthenticated } from "user/store";
import { refreshJwtToken, getUser } from "burde-api-content/burde-user-service";
import { signOutUser } from "page/user/service";
import CalendarContentHandler from "./calendar-content-handler";
import { User } from "appz";
import Cookies from "js-cookie";

type CalendarProps = {
    routeType: string;
    calendar: CalendarState;
    width: number;
    height: number;
    id: string;
    activeComponent: NavigationComponent;
    fastTrackStatus: boolean;
    previewedExtraPage: ExtraPage | null;
    isLoadingLayoutOptions: boolean;
    isAuthenticated: boolean;
    userHasLeftOwnerPageStep: boolean;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiralItem: SpiralItem | null;
    rubberbandItem: RubberBandItem | null;
    monthDividerInfo: MonthDividerType | null;
    ownerPage: OwnerPageType | null;
    ownerPageImageUrl: string | null;
    updateUser: User | null;
    domain: string;
    borderImage: ImageDetails | null;
} & typeof actionCreators &
    typeof burdeApiContentActionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators &
    typeof userActionCreators &
    RouteComponentProps<{
        step?: string;
        typeName?: string;
        sizeName?: string;
        location?: any;
        history?: any;
    }>;

const Calendar: React.FC<CalendarProps> = ({
    routeType,
    previewedExtraPage,
    calendar,
    match: {
        params: { step: routeStep },
    },
    location,
    userHasLeftOwnerPageStep,
    updateUserHasLeftOwnerPageStep,
    UpdateShowUserMenu,
    updateUser,
    coverImage,
    backCoverImage,
    customCoverImage,
    customBackImage,
    spiralItem,
    rubberbandItem,
    monthDividerInfo,
    domain,
    borderImage,
}) => {
    const resources = useResources();

    useEffect(() => {
        const validateBurdeJwtToken = async () => {
            const jwtToken = Cookies.get(COOKIE_USER_JWT);

            if (jwtToken) {
                // Get user.
                const user = await getUser();

                if (user === null || user.email === null) {
                    // Set unauthenticated
                    updateUser({
                        id: "",
                        roles: [],
                        authenticated: false,
                        userName: "",
                        email: "",
                        softDeleted: false,
                    });
                    Cookies.remove(COOKIE_USER_JWT, {
                        domain,
                    });
                    return;
                } else {
                    // Set authenticated
                    updateUser({
                        id: "",
                        roles: [],
                        authenticated: true,
                        userName: "",
                        email: user.email,
                        softDeleted: false,
                    });
                }

                const expired = isTokenExpired(jwtToken, Date.now());

                // Log out user if token has expired.
                if (expired) {
                    signOut();
                }

                // Check if token expires in the next 24 hours.
                const today = new Date();
                today.setDate(today.getDate() + 1);

                const expiresTomorrow = isTokenExpired(
                    jwtToken,
                    today.getTime(),
                );

                if (!expiresTomorrow) {
                    return;
                }

                // Refresh token because it expires soon.
                var newToken = await refreshJwtToken(jwtToken);

                // Sign out user if refresh failed.
                if (!newToken) {
                    signOut();
                    return;
                }

                Cookies.set(COOKIE_USER_JWT, newToken, {
                    expires: COOKIE_USER_EXPIRE_DAYS,
                    domain: domain,
                });

                const personalDates = await getDatesets();

                updatePersonalDateInfo(
                    personalDates !== null ? personalDates : [],
                );
            }
        };

        validateBurdeJwtToken().catch(console.error);

        const parsedQuery = queryString.parseUrl(location.search);

        if (parsedQuery.query["locked"] === "true") {
            document.body.classList.add("locked");
        }
    }, []);

    const signOut = async () => {
        const { success } = await signOutUser();
        if (success) {
            UpdateShowUserMenu(false);
            updateUser({
                id: "",
                roles: [],
                authenticated: false,
                userName: "",
                email: "",
                softDeleted: false,
            });

            Cookies.remove(COOKIE_USER_JWT, {
                domain,
            });

            window.location.href = "/";
        }
    };

    // Save temporary calendar if user has left the owner page step and have a correctly formatted email.
    useEffect(() => {
        if (userHasLeftOwnerPageStep) {
            // If not a valid email, there is no need to save the calendar.
            const userEmailIsValid = validateEmail(calendar.ownerPage.email);
            if (!userEmailIsValid) {
                updateUserHasLeftOwnerPageStep(false);
                return;
            }

            const calendarToBeSaved: CalendarState = { ...calendar };
            const storedTemporaryCalendar = localStorage.getItem(
                LOCAL_STORAGE_CONFIGURATION,
            );

            if (storedTemporaryCalendar) {
                const parsed: LocalStorageTemporaryCalendar = JSON.parse(
                    storedTemporaryCalendar,
                );
                const then = moment(parsed.timestamp);
                const now = moment(Date.now());

                const diffHours = moment.duration(now.diff(then)).asHours();
                if (diffHours < MaxTemporaryCalendarStorageHours) {
                    saveCalendar(
                        calendarToBeSaved,
                        parsed.temporaryCalendar.calendarId,
                        coverImage,
                        backCoverImage,
                        customCoverImage,
                        customBackImage,
                        spiralItem,
                        rubberbandItem,
                    );
                } else {
                    localStorage.removeItem(LOCAL_STORAGE_CONFIGURATION);
                    onCreateTemporaryCalendar(calendarToBeSaved);
                }
            } else {
                onCreateTemporaryCalendar(calendarToBeSaved);
            }

            updateUserHasLeftOwnerPageStep(false);
        }
    }, [userHasLeftOwnerPageStep]);

    const onCreateTemporaryCalendar = async (calendar: CalendarState) => {
        createTemporaryCalendar(
            calendar,
            coverImage,
            backCoverImage,
            customCoverImage,
            customBackImage,
            spiralItem,
            rubberbandItem,
        ).then((response) => {
            if (response !== null) {
                const temporaryCalendar: LocalStorageTemporaryCalendar = {
                    timestamp: moment(),
                    temporaryCalendar: response,
                };

                localStorage.setItem(
                    LOCAL_STORAGE_CONFIGURATION,
                    JSON.stringify(temporaryCalendar),
                );
            }
        });
    };

    const defaultRoute = getComponentRoute(
        NavigationComponents[0].route,
        calendar.calendarSize,
        calendar.calendarType,
    );

    const parsedQuery = queryString.parseUrl(location.search);

    const stepContent =
        parsedQuery.query.fasttrack && routeStep !== CalendarSteps.cover ? (
            <Redirect to={defaultRoute} push />
        ) : routeStep === CalendarSteps.cover ? (
            <CalendarCover
                calendar={calendar}
                coverImage={coverImage}
                backCoverImage={backCoverImage}
                customCoverImage={customCoverImage}
                customBackImage={customBackImage}
                spiral={spiralItem}
                rubberband={rubberbandItem}
            />
        ) : routeStep == CalendarSteps.ownerPage ? (
            <OwnerPage />
        ) : routeStep == CalendarSteps.insert ? (
            <CalendarInsert
                calendar={calendar}
                spiral={spiralItem}
                rubberband={rubberbandItem}
            />
        ) : routeStep === CalendarSteps.summary ? (
            <CalendarSummary
                calendar={calendar}
                coverImage={coverImage}
                backCoverImage={backCoverImage}
                customCoverImage={customCoverImage}
                customBackImage={customBackImage}
                spiral={spiralItem}
                rubberband={rubberbandItem}
                borderImage={borderImage}
            />
        ) : routeStep === CalendarSteps.monthDivider &&
          calendar.monthDivider !== null ? (
            <MonthDivider monthDivider={monthDividerInfo} spiral={spiralItem} />
        ) : routeStep === CalendarSteps.monthDivider ? (
            <CalendarInsert
                calendar={calendar}
                stepIsDimmed={true}
                dimmedText={resources["MonthDivider_DimmedText"]}
                spiral={spiralItem}
                rubberband={rubberbandItem}
            />
        ) : routeStep === CalendarSteps.extraPages &&
          previewedExtraPage !== null ? (
            <ExtraPages spiral={spiralItem} />
        ) : routeStep === CalendarSteps.extraPages ? (
            <CalendarInsert
                calendar={calendar}
                stepIsDimmed={true}
                dimmedText={resources["ExtraPages_DimmedText"]}
                spiral={spiralItem}
                rubberband={rubberbandItem}
            />
        ) : (
            <Redirect
                to={`/calendar/${DefaultSize.toLowerCase()}/${
                    CalendarSteps.cover
                }`}
                push
            />
        );

    return (
        <React.Fragment>
            <CalendarContentHandler />
            <CalendarUrlHandler routeType={routeType} />
            {stepContent}
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    activeComponent: getActiveNavigationComponent(state),
    fastTrackStatus: getFastTrackStatus(state),
    previewedExtraPage: getPreviewedExtraPage(state),
    isLoadingLayoutOptions: getIsLoadingLayoutOptions(state),
    isAuthenticated: getIsAuthenticated(state),
    coverImage: getCoverImage(state),
    backCoverImage: getBackCoverImage(state),
    customCoverImage: getCustomCoverImage(state),
    customBackImage: getCustomBackImage(state),
    spiralItem: getSpiralItem(state),
    rubberbandItem: getRubberbandItem(state),
    monthDividerInfo: getMonthDividerInfo(state),
    domain: getDomain(state),
    borderImage: getBorderImage(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
    ...burdeApiContentActionCreators,
    ...eventActionCreators,
    ...userActionCreators,
};

const componentWithRouter = withRouter(Calendar);

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(componentWithRouter);
